import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import Footer from "./Footer";
import { EMAIL, PHONE_DISPLAY, FadeUp, FloralCorner } from "./shared";

const SECTIONS = [
    {
        title: "1. Quem somos",
        text: "Este site é de responsabilidade de Maricleia Araújo, gerontóloga autônoma que atende em Curitiba e região metropolitana. Para fins da Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD), Maricleia Araújo é a controladora dos dados pessoais tratados por meio deste site.",
    },
    {
        title: "2. Quais dados coletamos",
        text: "Este site não possui cadastro, login nem armazena dados em servidores próprios. As informações que você digita no formulário de contato (nome, telefone e mensagem) são usadas apenas para montar uma mensagem que é aberta diretamente no seu aplicativo WhatsApp, endereçada à profissional. Nenhum dado do formulário fica salvo neste site.",
    },
    {
        title: "3. Finalidade do tratamento",
        text: "Os dados fornecidos voluntariamente pelo WhatsApp ou e-mail são utilizados exclusivamente para: responder ao seu contato, agendar avaliações, prestar os serviços de gerontologia e orientar famílias. Não utilizamos seus dados para envio de publicidade não solicitada.",
    },
    {
        title: "4. Compartilhamento de dados",
        text: "Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para fins comerciais. Ao usar o WhatsApp, suas informações trafegam pela plataforma Meta, conforme a política de privacidade do próprio aplicativo.",
    },
    {
        title: "5. Cookies",
        text: "Utilizamos apenas cookies estritamente necessários ao funcionamento do site e, mediante seu consentimento no banner exibido na primeira visita, cookies de análise de navegação. Você pode aceitar, recusar ou apagar os cookies a qualquer momento pelas configurações do seu navegador.",
    },
    {
        title: "6. Seus direitos (LGPD, art. 18)",
        text: "Você tem direito a: confirmar a existência de tratamento dos seus dados, acessá-los, corrigi-los, solicitar anonimização ou eliminação, revogar o consentimento e obter informações sobre compartilhamentos. Para exercer qualquer desses direitos, basta entrar em contato pelos canais abaixo.",
    },
    {
        title: "7. Segurança e retenção",
        text: "As conversas e dados compartilhados no atendimento são tratados com sigilo profissional e mantidos somente pelo tempo necessário à prestação do serviço e ao cumprimento de obrigações legais.",
    },
    {
        title: "8. Encarregado de dados (DPO)",
        text: `Encarregada pelo tratamento de dados pessoais: Maricleia Araújo. E-mail: ${EMAIL} · WhatsApp: ${PHONE_DISPLAY} · Curitiba/PR.`,
    },
    {
        title: "9. Alterações desta política",
        text: "Esta política pode ser atualizada periodicamente para refletir melhorias no site ou mudanças na legislação. A versão vigente estará sempre disponível nesta página, com a data da última revisão indicada abaixo.",
    },
];

const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        const prev = document.title;
        document.title = "Política de Privacidade | Maricleia Araújo Gerontóloga";
        return () => {
            document.title = prev;
        };
    }, []);

    return (
        <div className="relative min-h-screen" data-testid="privacy-page">
            <header className="sticky top-0 z-50 border-b border-lav bg-cream/85 backdrop-blur-md">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3 sm:px-8">
                    <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo-link">
                        <Logo size={42} />
                        <span className="leading-tight">
                            <span className="block font-serif text-lg font-bold text-plum">Maricleia Araújo</span>
                            <span className="block text-[11px] uppercase tracking-[0.22em] text-serenity">Gerontóloga</span>
                        </span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="privacy-back-button"
                        className="inline-flex items-center gap-2 rounded-full border border-lilac/60 bg-white/70 px-5 py-2.5 text-sm font-semibold text-plum transition-transform duration-300 hover:scale-[1.03]"
                    >
                        <ArrowLeft size={16} />
                        Voltar ao site
                    </Link>
                </div>
            </header>

            <main className="relative overflow-hidden py-16 sm:py-24">
                <div className="drift pointer-events-none absolute -left-32 top-10 -z-10 h-[28rem] w-[28rem] rounded-full bg-lav/60 blur-3xl" aria-hidden="true" />
                <div className="drift-slow pointer-events-none absolute -right-40 bottom-0 -z-10 h-[30rem] w-[30rem] rounded-full bg-serenitylight/40 blur-3xl" aria-hidden="true" />
                <FloralCorner className="pointer-events-none absolute -right-4 top-8 h-40 w-40 rotate-90 text-lilac/40" />

                <div className="mx-auto max-w-3xl px-5 sm:px-8">
                    <FadeUp className="text-center">
                        <span className="inline-flex items-center gap-2 rounded-full border border-lilac/50 bg-white/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-plum sm:text-xs">
                            <ShieldCheck size={14} className="text-serenity" />
                            LGPD · Lei nº 13.709/2018
                        </span>
                        <h1 className="mt-6 font-serif text-4xl font-bold leading-tight tracking-tight text-plum sm:text-5xl">
                            Política de <span className="italic text-serenity">Privacidade</span>
                        </h1>
                        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
                            Transparência no cuidado também com os seus dados. Entenda como este site trata informações pessoais.
                        </p>
                    </FadeUp>

                    <div className="mt-12 space-y-6">
                        {SECTIONS.map((s, i) => (
                            <FadeUp key={s.title} delay={Math.min(i * 0.06, 0.3)} className="rounded-3xl border border-lilac/40 bg-white p-6 shadow-md shadow-plum/5 sm:p-8" data-testid={`privacy-section-${i + 1}`}>
                                <h2 className="font-serif text-xl font-semibold text-plum sm:text-2xl">{s.title}</h2>
                                <p className="mt-3 text-base leading-relaxed text-ink sm:text-lg">{s.text}</p>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.2} className="mt-10 text-center">
                        <p className="text-sm text-ink/60">Última atualização: setembro de 2026.</p>
                    </FadeUp>
                </div>
            </main>

            <Footer base="/" />
        </div>
    );
};

export default PrivacyPolicy;
